import { NoteId, NoteIdsList } from '../types/notes';
import constants from '../constants';
import logger from '../logger';
import firebase from 'firebase/app';
const { SELECTED_NOTEBOOK } = constants;

// Removed unused functions: renameProp, camelize

export const getSelectedEntityID = (collection: { selected_stack: any[] }) => {
  if (typeof collection === 'undefined') {
    console.error('getSelectedEntityID undefined collection');
  }
  const { selected_stack = [] } = collection;

  const end = selected_stack.length - 1;
  if (!!selected_stack.length && end >= 0) {
    return selected_stack[end];
  } else {
    return null;
  }
};

export const vhFix = () => {
  // Use visualViewport.height when available — it reflects the area above the virtual keyboard
  // on Android PWA/standalone mode where window.innerHeight doesn't change when keyboard opens.
  const height = window.visualViewport?.height ?? window.innerHeight;
  const vh = height * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
};

export const vhFixWithResize = () => {
  const delay = 250;
  let timeout: any = null;
  vhFix();

  const handler = () => {
    clearTimeout(timeout);
    timeout = setTimeout(vhFix, delay);
  };

  // Listen to both window resize (browser mode) and visualViewport resize (PWA/overlay keyboard mode)
  window.addEventListener('resize', handler);
  window.visualViewport?.addEventListener('resize', handler);
};

/**
 * AOF two-array data model — Option B: selected_stack is pure recency history
 *
 * An Area of Focus collection (AOFCollection) tracks open notes with two arrays:
 *
 *   order_of_items  — canonical ordered list of every note currently open in the AOF.
 *                     The client is authoritative. Updated optimistically on open/close.
 *
 *   selected_stack  — recency history of explicitly user-selected notes, newest last.
 *                     The server is authoritative (API responses carry the updated stack).
 *                     A note can be open (in order_of_items) without ever appearing here
 *                     if the user never explicitly clicked it.
 *
 * Design contract (Option B):
 *   - selected_stack is NOT required to mirror order_of_items.
 *   - Never assume a note in order_of_items is also in selected_stack.
 *   - Always fall back to order_of_items when selected_stack is empty or unhelpful.
 *   - Clean up each array independently on note removal.
 *
 * See context/aof-data-model.md for the full design rationale.
 */

/**
 * Returns the most-recently explicitly-selected note that isn't the one being removed.
 *
 * selected_stack is newest-last, so we walk backwards. Returns null when the stack
 * is empty or contains only the note being removed — callers must fall back to
 * order_of_items in that case.
 */
export const getNoteIdToSelect = (selectedStack: any[], noteIdToRemove: any) => {
  if (!selectedStack || selectedStack.length === 0) {
    return null;
  }

  for (let i = selectedStack.length - 1; i >= 0; i--) {
    if (selectedStack[i] !== noteIdToRemove) {
      return selectedStack[i];
    }
  }

  return null;
};

/**
 * Removes a note from an AOFCollection and returns the next note to activate.
 *
 * Mutates AOFCollection in place (caller should deep-copy first).
 *
 * Removal is independent for each array:
 *   - Removes from order_of_items if present (the note is no longer open).
 *   - Removes from selected_stack if present (clean up recency history).
 * Neither removal depends on the note being in the other array.
 *
 * noteIdToSelect is the most-recently selected note still open after removal,
 * or null if selected_stack had nothing useful. A null return means the caller
 * should fall back to order_of_items[order_of_items.length - 1] (most recently
 * added open note).
 */
export const setLastSelectedItemIdInCollection = (
  AOFCollection: any,
  noteIdToRemove: any
) => {
  const orderOfItemsEntityLocation =
    AOFCollection.order_of_items.indexOf(noteIdToRemove);
  const selectedStackEntityLocation =
    AOFCollection.selected_stack.indexOf(noteIdToRemove);

  // Determine the next note to activate before mutating the arrays.
  const noteIdToSelect = getNoteIdToSelect(AOFCollection.selected_stack, noteIdToRemove);

  // Remove from order_of_items if the note is present there.
  if (orderOfItemsEntityLocation !== -1) {
    AOFCollection.order_of_items.splice(orderOfItemsEntityLocation, 1);
  }

  // Remove from selected_stack if the note is present there.
  // This is independent of the order_of_items check — a note may be in one
  // array but not the other (see Option B contract above).
  if (selectedStackEntityLocation !== -1) {
    AOFCollection.selected_stack.splice(selectedStackEntityLocation, 1);
  }

  return {
    AOFCollection,
    noteIdToSelect,
  };
};

export const isDesktop = (breakpoint: string) => {
  const isDesktop =
    !!(breakpoint === 'desktop') ||
    !!(breakpoint === 'desktopLarge') ||
    !!(breakpoint === 'desktopWide');

  return isDesktop;
};

export const isMobile = (breakpoint: string) => {
  const isMobile =
    !!(breakpoint === 'mobile') ||
    !!(breakpoint === 'mobileLandscape') ||
    !!(breakpoint === 'tablet') ||
    !!(breakpoint === 'tabletLandscape');

  return isMobile;
};

export const isBrowser = typeof window !== 'undefined';

export const getSelectedNotebookObject = ({ selectedNotebook, notebooks }: any) => {
  let selectedNotebookObject = null;
  if (
    typeof selectedNotebook === 'undefined' ||
    typeof notebooks === 'undefined' ||
    notebooks === null ||
    !Object.keys(notebooks).length
  ) {
    throw 'Missing required selectedNotebook or notebooks';
  }

  if (selectedNotebook === 'all_notes') {
    selectedNotebookObject = SELECTED_NOTEBOOK;
  } else {
    selectedNotebookObject = notebooks[selectedNotebook] || null;
  }

  return selectedNotebookObject;
};

// Removed unused function: addNoteToAreaOfFocusCollectionHelper

/*
 * https://developer.mozilla.org/en-US/docs/Web/API/VirtualKeyboard_API
 * To opt out of the automatic virtual keyboard behavior of the browser,
 * use navigator.virtualKeyboard.overlaysContent = true. The browser will no
 * longer automatically resize the viewport to make space for the virtual keyboard,
 * which will overlay your content instead.
 */
export const controlOnScreenKeyboard = () => {
  if ('virtualKeyboard' in navigator) {
    // navigator.virtualKeyboard.overlaysContent = true;
  }
};

/**
 * Sorts a list of notes based on various criteria
 * @param {Object} params - Sorting parameters
 * @param {Array} params.noteList - Array of notes to sort
 * @param {string} params.sortBy - Sort criteria: 'alphabetical', 'created', 'modified', 'mostUsed', 'lastUsed'
 * @param {string} params.sortDirection - Sort direction: 'asc' or 'desc'
 * @param {Object} params.usageStats - Optional usage statistics with usageCounts and lastUsed
 * @returns {Array} Sorted array of notes
 */
export const sortNotesByDateModified = ({
  noteList,
  sortBy = 'modified',
  sortDirection = 'desc',
  usageStats = { usageCounts: {}, lastUsed: {} },
}: {
  noteList: any[];
  sortBy?: 'alphabetical' | 'created' | 'modified' | 'mostUsed' | 'lastUsed';
  sortDirection?: 'asc' | 'desc';
  usageStats?: { usageCounts: Record<string, number>; lastUsed: Record<string, number> };
}) => {
  if (!noteList || !Array.isArray(noteList)) {
    return [];
  }

  try {
    const { usageCounts = {}, lastUsed = {} } = usageStats;
    
    return [...noteList].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'alphabetical':
          comparison = (a.note_title || '').localeCompare(b.note_title || '');
          break;

        case 'created':
          const dateA = a.date_created
            ? new Date(a.date_created).getTime()
            : 0;
          const dateB = b.date_created
            ? new Date(b.date_created).getTime()
            : 0;
          comparison = dateA - dateB;
          break;

        case 'modified':
          const modDateA = a.date_modified
            ? new Date(a.date_modified).getTime()
            : 0;
          const modDateB = b.date_modified
            ? new Date(b.date_modified).getTime()
            : 0;
          comparison = modDateA - modDateB;
          break;

        case 'mostUsed':
          const countA = usageCounts[a.note_id] || 0;
          const countB = usageCounts[b.note_id] || 0;
          comparison = countA - countB;
          break;

        case 'lastUsed':
          const lastA = lastUsed[a.note_id] || 0;
          const lastB = lastUsed[b.note_id] || 0;
          // Also check date_opened if available
          const openedA = a.date_opened
            ? new Date(a.date_opened).getTime()
            : 0;
          const openedB = b.date_opened
            ? new Date(b.date_opened).getTime()
            : 0;
          const finalA = Math.max(lastA, openedA);
          const finalB = Math.max(lastB, openedB);
          comparison = finalA - finalB;
          break;

        default:
          comparison = 0;
      }

      // Apply sort direction
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  } catch (error) {
    logger.error({ error, noteList, sortBy, sortDirection }, 'Error sorting notes');
    return noteList; // Return original list on error
  }
};

/**
 * Returns if a note is found within a list
 * @param { noteId, openNotes }
 */
export const isNoteOpen = ({ noteId, openNotes }: { noteId: NoteId, openNotes: NoteIdsList }): Boolean => {
  return openNotes && openNotes.includes(noteId);
};

export const notebookTreeHelper = ({ notebooks, notebookId, notes }: any) => {
  const notebookTree: any = {};
  const notebookTreeList: any = {};
  if (
    typeof notebooks === 'undefined' ||
    notebooks === null ||
    !Object.keys(notebooks).length
  ) {
    return {
      notebookTree,
      notebookTreeList,
    };
  }

  try {
    Object.keys(notebooks).forEach((notebookId) => {
      if (notebookId !== 'all_notes') {
        notebookTree[notebookId] = {};
      }
    });

    Object.values(notes).forEach((note: any) => {
      const { notebook_id, note_id } = note;
      if (notebookTree[notebook_id]) {
        notebookTree[notebook_id][note_id] = note;
      }
    });

    Object.keys(notebookTree).forEach((notebookId) => {
      notebookTreeList[notebookId] = Object.values(notebookTree[notebookId]);
    });

    // 'all_notes' is a virtual notebook representing every note in the account
    notebookTreeList['all_notes'] = Object.values(notes);
  } catch (error) {
    console.log(error);
  }

  return {
    notebookTree,
    notebookTreeList,
  };
};

export const alphabetizeAreasOfFocusCollectionObjects = ({ areasOfFocusCollections }: any) => {
  if (
    typeof areasOfFocusCollections === 'undefined' ||
    !Object.keys(areasOfFocusCollections).length
  ) {
    return {};
  }
  const areasOfFocusCollectionsArray = Object.values(areasOfFocusCollections);
  const sorted = areasOfFocusCollectionsArray.sort((a: any, b: any) => {
    if (a.area_of_focus_collection_name < b.area_of_focus_collection_name) {
      return -1;
    }
    if (a.area_of_focus_collection_name > b.area_of_focus_collection_name) {
      return 1;
    }
    return 0;
  });
  return sorted.reduce((acc: any, curr: any) => {
    acc[curr.area_of_focus_collection_id] = curr;
    return acc;
  }, {});
};

export const alphabetizeAreasOfFocusCollections = ({ areasOfFocusCollectionsIterable }: any) => {
  if (
    typeof areasOfFocusCollectionsIterable === 'undefined' ||
    !areasOfFocusCollectionsIterable.length
  ) {
    return [];
  }
  return areasOfFocusCollectionsIterable.sort((a: any, b: any) => {
    if (a.area_of_focus_collection_name < b.area_of_focus_collection_name) {
      return -1;
    }
    if (a.area_of_focus_collection_name > b.area_of_focus_collection_name) {
      return 1;
    }
    return 0;
  });
};

export const generateAreasOfFocusCollectionsIterable = (areasOfFocusCollections: any) => {
  if (
    typeof areasOfFocusCollections === 'undefined' ||
    !Object.keys(areasOfFocusCollections).length
  ) {
    return [];
  }
  return Object.values(areasOfFocusCollections);
};
